import { useCoreConfigQuery, useFetchBranchListQuery } from '@app/store/reducers/configs/configs.api'
import {
  OrderBusinessCardType,
  OrderDeliveryType,
  OrderType,
  useFetchCreateBusinessCardMutation,
} from '@app/store/reducers/salaryCards/businessCardOrder.api'
import Dialog from '@shared/Dialog'
import { InputField } from '@shared/InputField'
import { excludedRegex } from '@shared/libs/inputRegex'
import RadioField from '@shared/RadioField/RadioField'
import SelectGlobal from '@shared/Select'
import { Form, Row, Typography } from 'antd'
import React, { useEffect } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

function OrderBlockTwo() {
  const { t } = useTranslation()
  const { control, setValue, trigger, resetField } = useFormContext<OrderBusinessCardType>()

  const { data: branchList, isSuccess } = useFetchBranchListQuery()

  const [_, { isSuccess: createSalaryCardSuccess }] =
    useFetchCreateBusinessCardMutation({ fixedCacheKey: 'createBusinessCard' })

  const { data: orderNoInfo, isSuccess: orderNoInfoSuccess } =
    useCoreConfigQuery(undefined, { skip: false })

  const watchedValues = useWatch({
    control,
    defaultValue: {
      orderType: OrderType.ORDINARY,
      orderDeliveryType: OrderDeliveryType.BANK,
    },
    name: ['orderType', 'orderDeliveryType'],
  })

  const watchAccountBranch = useWatch({ control, name: 'accountBranch' })

  useEffect(() => {
    watchedValues[1] === OrderDeliveryType.COURIER &&
      resetField('transferredBranch', { defaultValue: '2' })
      watchedValues[0] === OrderType.URGENT &&
        watchedValues[1] === OrderDeliveryType.BANK &&
        resetField('transferredBranch', { defaultValue: '2' })
    if (
      watchedValues[0] === OrderType.ORDINARY &&
      watchedValues[1] === OrderDeliveryType.BANK
    ) {
      trigger('transferredBranch')
    }
  }, [watchedValues, orderNoInfoSuccess])

  useEffect(() => {
    if (orderNoInfoSuccess || createSalaryCardSuccess) {
      setValue(
        'accountBranch',
        branchList?.find((b) => b.value == orderNoInfo?.branchNo)?.value,
        {
          shouldValidate: true,
        }
      )
    }
  }, [
    watchAccountBranch,
    branchList,
    orderNoInfoSuccess,
    createSalaryCardSuccess,
  ])

 useEffect(() => {
    if (!orderNoInfo) return;
    const commission =
      orderNoInfo.businessCard.preparationTime?.[watchedValues[0]?.toLowerCase()]?.commission;
    setValue('commission.preparationFee', commission, { shouldValidate: true });
  }, [orderNoInfo, watchedValues[0]]);

  return (
    <Form
      style={{ height: '100%' }}
      labelCol={{ span: 8 }}
      wrapperCol={{ span: 24 }}
      layout="horizontal"
    >
      <Row
        gutter={[0, 20]}
        style={{
          height: '100%',
          boxSizing: 'border-box',
          backgroundColor: '#F3F6FA',
          borderRadius: 16,
          padding: 20,
          flexDirection: 'column',
        }}
      >
        <Controller
          name="orderType"
          control={control}
          defaultValue={OrderType.ORDINARY}
          render={({
            field: { name, onChange, value },
            fieldState: { invalid, error },
          }) => {
            return (
            <RadioField
              name={name}
              onChange={onChange}
              value={value}
              style={{ marginBottom: 0 }}
              validateStatus={invalid ? 'error' : 'success'}
              help={invalid ? error?.message : ''}
              label={
                <>
                  <Typography.Text>
                    {t('orders.salaryCardOrder.inputLabels.orderTypes')}
                    <br />
                    <span style={{ fontSize: '12px', color: '#747A83' }}>
                      {t('businessCardOrders.labels.commission')} {Number(orderNoInfo?.businessCard?.preparationTime?.[value.toLowerCase()]?.commission).toFixed(2)} AZN
                    </span>
                  </Typography.Text>
                </>
              }
              radioOptions={Object.entries(OrderType).map(([value, title]) => ({
                value,
                disabled: !(orderNoInfo?.businessCard?.preparationTime?.[value.toLowerCase()]?.active),
                title: t(`orders.salaryCardOrder.constants.${title}`),
                onClick(e) {
                  value === OrderType.ORDINARY && resetField('transferredBranch', { defaultValue: null })
                  value === OrderType.URGENT && Dialog.info({
                      title: t('common.attention'),
                      message: t(
                        'businessCardOrders.commissionFee'
                      ),
                      okText: t('orders.salaryCardOrder.buttonsLabels.close'),
                    })
                },
                // ...(value === OrderType.URGENT && {
                //   onClick: () =>
                //     Dialog.info({
                //       title: t('common.attention'),
                //       message: t(
                //         'businessCardOrders.commissionFee'
                //       ),
                //       okText: t('orders.salaryCardOrder.buttonsLabels.close'),
                //     }),
                // }),
              }))}
            />
          )
          }}
        />

        <Controller
          name="orderDeliveryType"
          control={control}
          defaultValue={OrderDeliveryType.BANK}
          render={({
            field: { name, onChange, value },
            fieldState: { invalid, error },
          }) => (
            <RadioField
              name={name}
              onChange={onChange}
              value={value}
              style={{ marginBottom: 0 }}
              validateStatus={invalid ? 'error' : 'success'}
              help={invalid ? error?.message : ''}
              style={{ marginBottom: 0 }}
              label={
                <>
                  <Typography.Text>
                    {t('businessCardOrders.labels.deliveryTypes')}
                    <span style={{ fontSize: '12px', color: '#747A83' }}>
                      <br />
                      {t('businessCardOrders.labels.commission')} {Number(orderNoInfo?.businessCard?.orderDelivery?.[value.toLowerCase()]?.commission).toFixed(2)} AZN
                    </span>
                  </Typography.Text>
                </>
              }
              radioOptions={Object.entries(OrderDeliveryType).map(
                ([value, title]) => ({
                  value,
                  disabled: !orderNoInfo?.businessCard?.orderDelivery?.[value.toLowerCase()]?.active,
                  title: t(`orders.salaryCardOrder.constants.${title}`),
                })
              )}
            />
          )}
        />
        <Controller
          name="accountBranch"
          control={control}
          render={({
            field: { value, ...field },
            fieldState: { invalid, error },
          }) => {
            const val = isSuccess
              ? branchList?.find((b) => b.value == value)?.label
              : ''

            return (
              <InputField
                disabled
                style={{ margin: 0 }}
                validateStatus={invalid ? 'error' : 'validating'}
                help={invalid ? error?.message : ''}
                label={t('businessCardOrders.labels.accountBranch')}
                size="large"
                placeholder={t(
                  'orders.salaryCardOrder.inputLabels.orderBranch'
                )}
                {...field}
                value={val}
              />
            )
          }}
        />

        <Controller
          name="transferredBranch"
          control={control}
          render={({
            field: { name, onChange, value, disabled },
            fieldState: { invalid, error },
          }) => {
            return (
              <SelectGlobal
                style={{ margin: 0 }}
                name={name}
                onChange={onChange}
                value={value}
                disabled={
                  !(
                    watchedValues[0] === OrderType.ORDINARY &&
                    watchedValues[1] === OrderDeliveryType.BANK
                  )
                }
                label={t(
                  'orders.salaryCardOrder.inputLabels.BranchTransferred'
                )}
                allowClear={false}
                placeholder={t('businessCardOrders.labels.select')}
                options={isSuccess ? branchList : []}
                validateStatus={invalid ? 'error' : 'success'}
                help={invalid ? t(error?.message) : ''}
              />
            )
          }}
        />
        <Controller
          name="note"
          control={control}
          render={({
            field: { name, onChange, ref, value },
            fieldState: { error, invalid },
          }) => (
            <InputField
              mask={/[\s\S]*/}
              maxLength={50}
              showCount
              style={{ margin: 0 }}
              maskOptions={{
                prepare: (str) => (excludedRegex.test(str) ? '' : str),
                commit: (value, masked) => {
                  masked._value = value
                },
              }}
              placeholder={t(
                'orders.salaryCardOrder.inputLabels.InformationNotTransmittedBank'
              )}
              label={t('orders.salaryCardOrder.inputLabels.note')}
              name={name}
              onChange={onChange}
              ref={ref}
              value={value as string}
              validateStatus={invalid ? 'error' : 'success'}
              help={invalid ? error?.message : ''}
            />
          )}
        />
      </Row>
    </Form>
  )
}

export default OrderBlockTwo
